import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { RootRoute } from './features/landing/RootRoute.js';

// feat: public landing · C — the entry chunk is the public shell only:
// router + the root split. The landing and the authenticated app (supabase
// auth, login / signup, TanStack Query, Layout, coach WebSocket, every page)
// are separate lazy chunks, so a visitor on "/" downloads no app code.
const AuthedApp = lazy(() => import('./AuthedApp.js').then((m) => ({ default: m.AuthedApp })));
// Corrida Billing — páginas públicas del chunk de la landing: precios y legales.
const PricingPage = lazy(() =>
  import('./features/landing/PricingPage.js').then((m) => ({ default: m.PricingPage })),
);
const LegalPage = lazy(() =>
  import('./features/landing/LegalPage.js').then((m) => ({ default: m.LegalPage })),
);

function ShellSkeleton(): JSX.Element {
  return (
    <div className="min-h-screen bg-nt-bg p-8">
      <div className="space-y-4">
        <div className="h-8 w-64 animate-pulse rounded bg-nt-card-elevated" />
        <div className="h-[260px] animate-pulse rounded-xl border border-nt-border bg-nt-card" />
        <div className="h-[380px] animate-pulse rounded-xl border border-nt-border bg-nt-card" />
      </div>
    </div>
  );
}

export default function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes — the landing lives in its own lazy chunk behind
            RootRoute; the old /landing and /pricing pages fold into it. */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/landing" element={<Navigate to="/" replace />} />
        <Route path="/pricing" element={<Navigate to="/precios" replace />} />
        <Route
          path="/precios"
          element={
            <Suspense fallback={<ShellSkeleton />}>
              <PricingPage />
            </Suspense>
          }
        />
        {(
          [
            ['/terminos', 'terms'],
            ['/privacidad', 'privacy'],
            ['/reembolsos', 'refunds'],
          ] as const
        ).map(([path, doc]) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<ShellSkeleton />}>
                <LegalPage doc={doc} />
              </Suspense>
            }
          />
        ))}
        {/* Everything else: login / signup + the authenticated app (own Routes + catch-all). */}
        <Route
          path="/*"
          element={
            <Suspense fallback={<ShellSkeleton />}>
              <AuthedApp />
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
